/**
 * Analytics Dashboard
 * Content performance tracking, A/B variant analytics, and platform engagement metrics.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, BarChart2, RefreshCw, Award, Target,
  Zap, Clock, CheckCircle, Eye, ThumbsUp, Brain,
  Calendar, AlertCircle, ArrowUp, ArrowDown,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  LinkedIn: '#0077b5',
  'Twitter/X': '#1da1f2',
  Instagram: '#e1306c',
  TikTok: '#ff0050',
  'Blog/Article': '#a68eff',
  Newsletter: '#3dffa0',
  YouTube: '#fe2c55',
  Facebook: '#1877f2',
};

const PIE_COLORS = ['#a68eff', '#3dffa0', '#f97316', '#60a5fa', '#ff5370', '#f0c27f'];

function MetricCard({ label, value, sub, color, icon: Icon, trend }) {
  return (
    <div className="rounded-2xl p-4 flex items-center gap-3.5"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)' }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-end gap-1.5">
          <div className="text-2xl font-black text-white">{value}</div>
          {trend !== undefined && (
            <div className="flex items-center gap-0.5 text-[10px] font-bold mb-1"
              style={{ color: trend >= 0 ? '#3dffa0' : '#ff5370' }}>
              {trend >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className="text-xs truncate" style={{ color: 'var(--llmc-text-muted)' }}>{label}</div>
        {sub && <div className="text-[10px]" style={{ color: `${color}80` }}>{sub}</div>}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 text-xs"
      style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
      <div className="font-bold mb-1">{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [queue, setQueue] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [perfData, setPerfData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, aRes] = await Promise.all([
        axios.get(`${API}/api/social/queue/all`).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/api/discord/activity`).catch(() => ({ data: { activity: [] } })),
      ]);
      setQueue(qRes.data?.posts || []);
      setActivity(aRes.data?.activity || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const runAnalysis = async () => {
    try {
      const { data } = await axios.post(`${API}/api/performance/analyze`, {
        brand_id: 'default',
        metric_type: 'all',
      });
      setPerfData(data);
    } catch { }
  };

  useEffect(() => { load(); }, [load]);

  // Derived metrics
  const total = queue.length;
  const published = queue.filter(q => q.status === 'published').length;
  const approved = queue.filter(q => q.status === 'approved').length;
  const declined = queue.filter(q => q.status === 'declined').length;
  const pending = queue.filter(q => q.status === 'pending_review').length;
  const totalDecided = approved + declined;
  const approvalRate = totalDecided > 0 ? Math.round((approved / totalDecided) * 100) : 0;

  // Content by platform
  const byPlatform = queue.reduce((acc, item) => {
    const p = item.platform || 'Other';
    if (!acc[p]) acc[p] = { platform: p, total: 0, published: 0, approved: 0, declined: 0 };
    acc[p].total++;
    if (item.status === 'published') acc[p].published++;
    if (item.status === 'approved') acc[p].approved++;
    if (item.status === 'declined') acc[p].declined++;
    return acc;
  }, {});
  const platformChartData = Object.values(byPlatform).sort((a, b) => b.total - a.total);

  // Content by type
  const byType = queue.reduce((acc, item) => {
    const t = item.content_type || 'post';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const typeChartData = Object.entries(byType).map(([name, value]) => ({ name, value }));

  // Activity by day (last 14 days)
  const activityByDay = (() => {
    const byDay = {};
    activity.forEach(a => {
      const day = a.timestamp ? new Date(a.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Unknown';
      if (!byDay[day]) byDay[day] = { day, approve: 0, decline: 0, feedback: 0, send: 0 };
      if (a.action === 'approve') byDay[day].approve++;
      else if (a.action === 'decline') byDay[day].decline++;
      else if (a.action === 'feedback') byDay[day].feedback++;
      else if (a.action === 'notification_sent') byDay[day].send++;
    });
    return Object.values(byDay).slice(-14);
  })();

  // Top topics (most recurring)
  const topicCounts = queue.reduce((acc, item) => {
    const t = item.topic || item.title;
    if (t) {
      const key = t.substring(0, 40);
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {});
  const topTopics = Object.entries(topicCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([topic, count]) => ({ topic, count }));

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'platforms', label: 'By Platform' },
    { id: 'activity', label: 'Activity Timeline' },
    { id: 'topics', label: 'Top Topics' },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4" style={{ borderBottom: '1px solid var(--llmc-border)', background: 'rgba(10,10,20,0.97)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(240,194,127,0.25), rgba(249,115,22,0.1))', border: '1px solid rgba(240,194,127,0.3)' }}>
              <TrendingUp size={20} style={{ color: '#f0c27f' }} />
            </div>
            <div>
              <div className="font-black text-white text-sm">Analytics Dashboard</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                Content performance · Platform breakdown · Approval trends
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runAnalysis}
              data-testid="run-analysis-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(124,94,250,0.1)', border: '1px solid rgba(124,94,250,0.25)', color: '#a68eff' }}>
              <Brain size={12} /> AI Analysis
            </button>
            <button onClick={load} disabled={loading}
              data-testid="analytics-refresh-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-3">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: activeTab === tab.id ? 'rgba(240,194,127,0.15)' : 'transparent',
                color: activeTab === tab.id ? '#f0c27f' : 'rgba(255,255,255,0.4)',
                border: activeTab === tab.id ? '1px solid rgba(240,194,127,0.3)' : '1px solid transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* AI Analysis Result */}
        {perfData && (
          <div className="rounded-2xl p-4"
            style={{ background: 'rgba(124,94,250,0.06)', border: '1px solid rgba(124,94,250,0.2)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Brain size={14} style={{ color: '#a68eff' }} />
              <span className="text-xs font-bold" style={{ color: '#a68eff' }}>Einstein Performance Analysis</span>
              <span className="text-[10px] ml-auto" style={{ color: 'rgba(255,255,255,0.3)' }}>
                Score: {perfData.performance_score}/100
              </span>
            </div>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {perfData.insights?.[0] || perfData.message || 'Analysis complete. Check performance metrics below.'}
            </p>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-4 gap-3" data-testid="analytics-overview-stats">
              <MetricCard label="Total Content" value={total} color="#a68eff" icon={BarChart2} />
              <MetricCard label="Published" value={published} color="#3dffa0" icon={Zap} sub={`${total > 0 ? Math.round((published / total) * 100) : 0}% of total`} />
              <MetricCard label="Approval Rate" value={`${approvalRate}%`} color={approvalRate >= 70 ? '#3dffa0' : '#f97316'} icon={Target} sub={`${totalDecided} decisions`} />
              <MetricCard label="Pending Review" value={pending} color="#f0c27f" icon={Clock} sub={pending > 0 ? 'Needs attention' : 'Queue clear'} />
            </div>

            {/* Content Type Distribution */}
            <div className="grid grid-cols-2 gap-5">
              <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
                <div className="text-xs font-bold text-white mb-4">Content Type Distribution</div>
                {typeChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={typeChartData} cx="50%" cy="50%" outerRadius={70} paddingAngle={3} dataKey="value">
                        {typeChartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend formatter={(v) => <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{v}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-48 flex items-center justify-center text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No data yet</div>
                )}
              </div>

              <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
                <div className="text-xs font-bold text-white mb-4">Content Status Funnel</div>
                <div className="space-y-3">
                  {[
                    { label: 'Generated', count: total, color: '#a68eff', pct: 100 },
                    { label: 'Approved', count: approved + published, color: '#3dffa0', pct: total > 0 ? Math.round(((approved + published) / total) * 100) : 0 },
                    { label: 'Published', count: published, color: '#f0c27f', pct: total > 0 ? Math.round((published / total) * 100) : 0 },
                    { label: 'Declined', count: declined, color: '#ff5370', pct: total > 0 ? Math.round((declined / total) * 100) : 0 },
                  ].map(({ label, count, color, pct }) => (
                    <div key={label}>
                      <div className="flex items-center justify-between text-[10px] mb-1">
                        <span style={{ color: 'rgba(255,255,255,0.5)' }}>{label}</span>
                        <span style={{ color }}>{count} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Platforms Tab */}
        {activeTab === 'platforms' && (
          <div className="space-y-5">
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
              <div className="text-xs font-bold text-white mb-4">Content Volume by Platform</div>
              {platformChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={platformChartData} margin={{ left: -15, top: 5, right: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="platform" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total" name="Total" fill="#a68eff" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="published" name="Published" fill="#3dffa0" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="declined" name="Declined" fill="#ff5370" radius={[3, 3, 0, 0]} />
                    <Legend formatter={(v) => <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{v}</span>} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-60 flex items-center justify-center text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No platform data yet</div>
              )}
            </div>

            <div className="grid gap-2">
              {platformChartData.map(p => {
                const color = PLATFORM_COLORS[p.platform] || '#a68eff';
                const rate = (p.approved + p.published) > 0 ? Math.round(((p.approved + p.published) / p.total) * 100) : 0;
                return (
                  <div key={p.platform} className="flex items-center gap-4 px-4 py-3 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <div className="font-semibold text-xs text-white w-28">{p.platform}</div>
                    <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-full rounded-full" style={{ width: `${rate}%`, background: color }} />
                    </div>
                    <div className="text-xs w-24 text-right" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {p.total} posts · <span style={{ color }}>{rate}% approved</span>
                    </div>
                  </div>
                );
              })}
              {platformChartData.length === 0 && (
                <div className="text-center py-8 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No platform data yet. Generate and queue some content first.</div>
              )}
            </div>
          </div>
        )}

        {/* Activity Timeline Tab */}
        {activeTab === 'activity' && (
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
            <div className="text-xs font-bold text-white mb-4">Discord Approval Activity (Last 14 Days)</div>
            {activityByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={activityByDay} margin={{ left: -15, top: 5, right: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="approve" name="Approved" stroke="#3dffa0" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="decline" name="Declined" stroke="#ff5370" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="feedback" name="Feedback" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} />
                  <Legend formatter={(v) => <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{v}</span>} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex flex-col items-center justify-center gap-3">
                <AlertCircle size={24} style={{ color: 'rgba(255,255,255,0.15)' }} />
                <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No Discord activity yet</div>
                <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>Send content to Discord via the Review Dashboard to start tracking</div>
              </div>
            )}
          </div>
        )}

        {/* Top Topics Tab */}
        {activeTab === 'topics' && (
          <div className="space-y-3">
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
              <div className="text-xs font-bold text-white mb-4">Most Generated Topics</div>
              {topTopics.length > 0 ? (
                <div className="space-y-2.5">
                  {topTopics.map(({ topic, count }, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black flex-shrink-0"
                        style={{ background: `${PIE_COLORS[i]}20`, color: PIE_COLORS[i] }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-white truncate">{topic}</div>
                        <div className="mt-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <div className="h-full rounded-full"
                            style={{ width: `${Math.round((count / topTopics[0].count) * 100)}%`, background: PIE_COLORS[i] }} />
                        </div>
                      </div>
                      <div className="text-xs font-bold flex-shrink-0" style={{ color: PIE_COLORS[i] }}>{count}x</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-center py-8" style={{ color: 'var(--llmc-text-muted)' }}>
                  No topic data yet. Generate some content first.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
